import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import StatusBadge, { STATUS_LABELS } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Download, Filter, MessageSquare, Inbox, Archive, FolderOpen, Plus } from "lucide-react";
import { getToken } from "@/lib/auth.jsx";

export default function Dashboard() {
    const { user } = useAuth();
    const isMedarbejder = user?.role === "medarbejder";
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [suppliers, setSuppliers] = useState([]);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [supplierFilter, setSupplierFilter] = useState("all");
    const [awaitingFilter, setAwaitingFilter] = useState(false);
    const [brandFilter, setBrandFilter] = useState("all");
    const [view, setView] = useState("active"); // "active" | "archived"

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { view };
            if (q) params.q = q;
            if (statusFilter && statusFilter !== "all") params.status = statusFilter;
            if (supplierFilter && supplierFilter !== "all") params.supplier_id = supplierFilter;
            if (brandFilter && brandFilter !== "all") params.brand = brandFilter;
            if (awaitingFilter) params.awaiting_reply = "true";
            const [cs, st, sp] = await Promise.all([
                api.get("/complaints", { params }),
                api.get("/stats/dashboard"),
                api.get("/suppliers"),
            ]);
            setItems(cs.data.items);
            setStats(st.data);
            setSuppliers(sp.data);
        } finally {
            setLoading(false);
        }
    }, [q, statusFilter, supplierFilter, brandFilter, awaitingFilter, view]);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
    }, [load]);

    const exportCsv = () => {
        const token = getToken();
        const params = new URLSearchParams();
        params.set("view", view);
        if (q) params.set("q", q);
        if (statusFilter !== "all") params.set("status", statusFilter);
        if (supplierFilter !== "all") params.set("supplier_id", supplierFilter);
        if (brandFilter !== "all") params.set("brand", brandFilter);
        params.set("auth", token);
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/complaints/export/csv?${params.toString()}`;
        window.open(url, "_blank");
    };

    const isArchiveView = view === "archived";

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-medium text-[#2C2A29]">
                        {isArchiveView ? "Arkiv" : "Reklamationer"}
                    </h1>
                    <p className="text-sm text-[#78736E] mt-1">
                        {isArchiveView ? "Afsluttede sager der er flyttet til arkivet" : "Oversigt over alle aktive sager"}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {!isMedarbejder && (
                        <Link to="/sag/ny">
                            <Button className="bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="new-case-button">
                                <Plus className="w-4 h-4 mr-2" />
                                Ny reklamation
                            </Button>
                        </Link>
                    )}
                    <Button variant="outline" onClick={exportCsv} data-testid="export-csv-button" className="border-[#E5E1DA] hover:bg-[#F0EBE1]">
                        <Download className="w-4 h-4 mr-2" />
                        Eksportér CSV
                    </Button>
                </div>
            </div>

            {/* View toggle */}
            <div className="inline-flex bg-white border border-[#E5E1DA] rounded-lg p-1" data-testid="view-toggle">
                <button
                    type="button"
                    onClick={() => { setView("active"); setAwaitingFilter(false); }}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                        view === "active" ? "bg-[#4A4641] text-white" : "text-[#5C5854] hover:bg-[#F0EBE1]"
                    }`}
                    data-testid="view-active"
                >
                    <FolderOpen className="w-4 h-4" />
                    Aktive
                    {stats?.total != null && <span className={`text-xs px-1.5 py-0.5 rounded ${view === "active" ? "bg-white/20" : "bg-[#F0EBE1]"}`}>{stats.total}</span>}
                </button>
                <button
                    type="button"
                    onClick={() => { setView("archived"); setAwaitingFilter(false); }}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                        view === "archived" ? "bg-[#4A4641] text-white" : "text-[#5C5854] hover:bg-[#F0EBE1]"
                    }`}
                    data-testid="view-archived"
                >
                    <Archive className="w-4 h-4" />
                    Arkiv
                    {stats?.archived != null && <span className={`text-xs px-1.5 py-0.5 rounded ${view === "archived" ? "bg-white/20" : "bg-[#F0EBE1]"}`}>{stats.archived}</span>}
                </button>
            </div>

            {/* Stats - only shown for active view */}
            {!isArchiveView && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard label="Aktive sager" value={stats?.total ?? "-"} />
                    <StatCard label="Åbne sager" value={stats?.open ?? "-"} />
                    <StatCard
                        label="Afventer svar"
                        value={stats?.awaiting_reply ?? 0}
                        highlight={(stats?.awaiting_reply ?? 0) > 0}
                        onClick={() => setAwaitingFilter(!awaitingFilter)}
                        active={awaitingFilter}
                        icon={<MessageSquare className="w-4 h-4" />}
                    />
                    <StatCard label="Godkendt/Lukket" value={(stats?.by_status?.godkendt ?? 0) + (stats?.by_status?.lukket ?? 0)} />
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-[#E5E1DA] rounded-xl p-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#78736E]" />
                    <Input
                        placeholder="Søg på sagsnummer, kunde eller ordre..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 border-[#E5E1DA]"
                        data-testid="dashboard-search-input"
                    />
                </div>
                {!isArchiveView && (
                    <Button
                        variant={awaitingFilter ? "default" : "outline"}
                        onClick={() => setAwaitingFilter(!awaitingFilter)}
                        data-testid="awaiting-filter-button"
                        className={awaitingFilter
                            ? "bg-[#993C3C] hover:bg-[#7E2F2F] text-white border-0"
                            : "border-[#E5E1DA] hover:bg-[#F0EBE1]"
                        }
                    >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        {awaitingFilter ? "Viser kun afventer svar" : "Kun afventer svar"}
                    </Button>
                )}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[200px] border-[#E5E1DA]" data-testid="dashboard-status-filter">
                        <Filter className="w-4 h-4 mr-1" />
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Alle statusser</SelectItem>
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                    <SelectTrigger className="w-[200px] border-[#E5E1DA]" data-testid="dashboard-supplier-filter">
                        <SelectValue placeholder="Leverandør" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Alle leverandører</SelectItem>
                        {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={brandFilter} onValueChange={setBrandFilter}>
                    <SelectTrigger className="w-[180px] border-[#E5E1DA]" data-testid="dashboard-brand-filter">
                        <SelectValue placeholder="Formular" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all" data-testid="brand-filter-all">Alle formularer</SelectItem>
                        <SelectItem value="gardinbussen" data-testid="brand-filter-gardinbussen">Gardinbussen</SelectItem>
                        <SelectItem value="gardinfabrikken" data-testid="brand-filter-gardinfabrikken">Gardinfabrikken</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="bg-white border border-[#E5E1DA] rounded-xl overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-[#F4F2EF] hover:bg-[#F4F2EF] border-[#E5E1DA]">
                            <TableHead className="w-[36px]"></TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Sagsnr</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Formular</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">{isArchiveView ? "Arkiveret" : "Dato"}</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Kunde</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Ordre</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Produkt</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Status</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Ansvarlig</TableHead>
                            <TableHead className="text-[#78736E] font-semibold uppercase text-[10px] tracking-wider">Leverandør</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={10} className="text-center text-[#78736E] py-8">Indlæser...</TableCell>
                            </TableRow>
                        )}
                        {!loading && items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={10} className="text-center text-[#78736E] py-12">
                                    {isArchiveView ? (
                                        <div className="flex flex-col items-center gap-2">
                                            <Archive className="w-8 h-8 text-[#D2C9B9]" />
                                            <div>Arkivet er tomt</div>
                                            <div className="text-xs">Når du arkiverer afsluttede sager, vises de her</div>
                                        </div>
                                    ) : awaitingFilter ? (
                                        <div className="flex flex-col items-center gap-2">
                                            <Inbox className="w-8 h-8 text-[#D2C9B9]" />
                                            <div>Ingen sager afventer svar fra kundeservice</div>
                                            <div className="text-xs">Alt er besvaret 🎉</div>
                                        </div>
                                    ) : "Ingen sager fundet"}
                                </TableCell>
                            </TableRow>
                        )}
                        {items.map((c) => {
                            const awaitingCustomer = !!c.awaiting_agent_reply || c.status === "ny";
                            const awaitingInternal = !!c.awaiting_internal_reply;
                            const awaiting = awaitingCustomer || awaitingInternal;
                            const dotTitle = awaitingCustomer && awaitingInternal
                                ? "Afventer svar fra kundeservice (kunde + leverandør/medarbejder)"
                                : awaitingCustomer
                                    ? (c.status === "ny" ? "Ny sag — afventer behandling" : "Kunden afventer svar")
                                    : "Leverandør/medarbejder har svaret";
                            const dotColor = awaitingCustomer ? "#993C3C" : "#6B5B95";
                            const dateField = isArchiveView ? c.archived_at : c.created_at;
                            return (
                                <TableRow
                                    key={c.id}
                                    className={`border-[#E5E1DA] ${awaiting && !isArchiveView ? "bg-[#FEF3C7]/40 hover:bg-[#FEF3C7]/60" : "hover:bg-[#FAFAF9]"}`}
                                    data-testid={`complaint-row-${c.case_number}`}
                                >
                                    <TableCell className="px-3">
                                        {awaiting && !isArchiveView && (
                                            <span
                                                className="block w-2 h-2 rounded-full"
                                                style={{ backgroundColor: dotColor }}
                                                title={dotTitle}
                                                data-testid={`awaiting-dot-${c.case_number}`}
                                            />
                                        )}
                                        {isArchiveView && (
                                            <Archive className="w-3.5 h-3.5 text-[#78736E]" />
                                        )}
                                    </TableCell>
                                    <TableCell className="font-mono text-sm font-semibold text-[#2C2A29]">
                                        <Link to={`/sag/${c.id}`} className="hover:underline" data-testid={`open-case-${c.case_number}`}>
                                            {c.case_number}
                                        </Link>
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {c.brand === "gardinfabrikken" ? (
                                            <span
                                                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-[#2C2A29] text-white"
                                                data-testid={`brand-badge-${c.case_number}`}
                                                data-brand="gardinfabrikken"
                                            >
                                                Gardinfabrikken
                                            </span>
                                        ) : (
                                            <span
                                                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-[#F0EBE1] text-[#4A4641]"
                                                data-testid={`brand-badge-${c.case_number}`}
                                                data-brand="gardinbussen"
                                            >
                                                Gardinbussen
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-sm text-[#5C5854]">{(dateField || "").slice(0, 10)}</TableCell>
                                    <TableCell className="text-sm text-[#2C2A29] font-medium">
                                        <div className="flex items-center gap-2">
                                            <span>{c.customer_name}</span>
                                            {awaiting && !isArchiveView && (
                                                <span
                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-white text-[10px] font-semibold uppercase tracking-wider"
                                                    style={{ backgroundColor: awaitingCustomer ? "#993C3C" : "#6B5B95" }}
                                                    title={awaitingCustomer ? "Kunden afventer svar" : "Leverandør/medarbejder har svaret"}
                                                >
                                                    <MessageSquare className="w-2.5 h-2.5" />
                                                    {awaitingCustomer ? "Kunde" : "Intern"}
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-sm text-[#5C5854]">{c.order_number || "-"}</TableCell>
                                    <TableCell className="text-sm text-[#5C5854]">
                                        {c.products?.[0]?.product_type}
                                        {c.products?.length > 1 && <span className="text-[#78736E]"> +{c.products.length - 1}</span>}
                                    </TableCell>
                                    <TableCell>
                                        <StatusBadge
                                            status={
                                                (!!c.awaiting_agent_reply || !!c.awaiting_internal_reply) && c.status !== "ny" && !c.archived
                                                    ? "afventer_svar"
                                                    : c.status
                                            }
                                        />
                                    </TableCell>
                                    <TableCell className="text-sm text-[#5C5854]">{c.assigned_to_name || "-"}</TableCell>
                                    <TableCell className="text-sm text-[#5C5854]">{c.supplier_name || "-"}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

function StatCard({ label, value, highlight, onClick, active, icon }) {
    const interactive = !!onClick;
    const cls = highlight
        ? "bg-[#993C3C] border-[#993C3C] text-white"
        : active
        ? "bg-[#4A4641] border-[#4A4641] text-white"
        : "bg-white border-[#E5E1DA]";
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={!interactive}
            className={`text-left border rounded-xl p-5 transition-all ${cls} ${interactive ? "hover:-translate-y-px hover:shadow-md cursor-pointer" : "cursor-default"}`}
            data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${highlight || active ? "text-white/80" : "text-[#78736E]"}`}>
                {icon}
                {label}
            </div>
            <div className={`font-heading text-3xl font-medium mt-1 ${highlight || active ? "text-white" : "text-[#2C2A29]"}`}>
                {value}
            </div>
        </button>
    );
}
